import { useMutation } from '@apollo/client';
import { Body, Button, Card, LargeTitle, LegalBody, palettes, useFlashMessage } from '@indigo-ag/component-module';
import {
  AgFinancing,
  AgFinancingCreditAnalysisStatus,
  AgFinancingDocumentType,
  AgFinancingFileFolder,
  AgFinancingFilesContext,
  AgFinancingInput,
  CreditInput,
  Phase2
} from '@indigo-ag/schema';
import { ExecutionResult } from 'graphql';
import React, { useContext, useState } from 'react';

import i18next from 'core/i18n';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  financingAgFinancing,
  updateCreditInfoFromInnovation,
  upsertFinancingAgFinancing
} from 'features/brazil-grower/queries';
import { CreditUtil, createStylesHook, text, utils } from 'shared';
import { PhaseWrapper } from '../../../shared/PhaseWrapper';
import { SendAnalysisModal } from '../../../shared/SendAnalysisModal/SendAnalysisModal';
import {
  ClientDataSection,
  CreditAnalysisStatusTag,
  ExploredAreas,
  FinancialInformationSection,
  IncomeStatementSection,
  JudicialActionsSection,
  NotesSection,
  Participation,
  PesquisaSerasa,
  ScoreSection,
  ToggleOnHoldCreditAnalysis,
  creditUtils
} from '../../../shared/credit-analysis';
import { MultipleFileUploadResult, ProgressDictionary, uploadFilesToAgFinancing } from '../../../utils';
import { GuarantorsSection } from '../sections/GuarantorsSection/GuarantorsSection';
import { getPesquisaSerasaInfoFromCredit } from '../../../shared/credit-analysis/shared/utils';

const useStyles = createStylesHook(() => ({
  pollDataWrapper: {
    display: 'flex',
    alignItems: 'baseline',
    marginTop: '20px'
  },
  colorGray: {
    color: `${palettes.brand.gray[700]} !important`
  }
}));

interface ICreditAnalysisContext {
  updateCredit: (newCreditInfo: Partial<CreditInput>) => Promise<ExecutionResult<any>>;
  uploadFilesToCredit: (
    files: File[],
    context: AgFinancingFilesContext,
    documentType?: AgFinancingDocumentType,
    onUploadProgress?: (progress: ProgressDictionary) => void
  ) => Promise<MultipleFileUploadResult>;
}

export const CreditAnalysisContext = React.createContext<ICreditAnalysisContext>({
  updateCredit: () => Promise.resolve({}),
  uploadFilesToCredit: () => Promise.resolve({ fulfilled: [], rejected: [] })
});

interface CreditAnalysisProps {
  credit: Phase2['credit'];
}

export const CreditAnalysis: React.FunctionComponent<CreditAnalysisProps> = ({ credit }) => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * State
   */
  const [sendAnalysisModalOpen, setSendAnalysisModalOpen] = useState(false);

  /**
   * Finance Process Context
   */
  const { agFinancingId, agFinance, contactRolesBackgroundChecksQuery } = useContext(FinanceProcessContext);
  const contactRoles = contactRolesBackgroundChecksQuery?.data?.opportunityContactRoles;
  const serasaDataApi = contactRolesBackgroundChecksQuery?.data?.serasaReport;
  const hasSerasaApiData = !!contactRolesBackgroundChecksQuery?.data?.serasaReport;
  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);
  const [updateCreditInfoFromInnovationFn, { loading }] = useMutation(updateCreditInfoFromInnovation);

  const closeSendAnalysisModal = () => {
    setSendAnalysisModalOpen(false);
  };

  const handleSendAnalysis = () => {
    setSendAnalysisModalOpen(true);
  };

  const { addFlashMessage } = useFlashMessage();
  const handlePollInnovationData = () => {
    if (!credit?.id) {
      addFlashMessage({
        content: `${text.toTitleCase(i18next.t('errorPollingInnovationData'))}: ${i18next.t(
          'errorPollingInnovationDataValues'
        )}`,
        title: '',
        variant: 'error'
      });

      return;
    }

    updateCreditInfoFromInnovationFn({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: financingAgFinancing,
          variables: {
            agFinancingId
          }
        }
      ],
      variables: {
        creditId: credit.id
      }
    })
      .then(() => {
        addFlashMessage({
          content: text.toTitleCase(i18next.t('innovationDataPolled')),
          title: '',
          variant: 'success'
        });
      })
      .catch(e => {
        addFlashMessage({
          content: `${text.toTitleCase(i18next.t('errorPollingInnovationData'))} - ${e?.message}.`,
          title: '',
          variant: 'error'
        });
      });
  };

  const updateCredit = (newCreditInfo: Partial<CreditInput>) => {
    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinancingId,
      phase2: {
        credit: {
          ...newCreditInfo
        }
      }
    };

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    });
  };

  const uploadFilesToCredit = uploadFilesToAgFinancing({
    agFinancingId,
    folder: AgFinancingFileFolder.CREDIT
  });

  const sendAnalysis = (notes: string) => {
    return updateCredit({
      isDone: true,
      status: AgFinancingCreditAnalysisStatus.COMPLETED,
      notes
    });
  };

  const analysisNotSent = CreditUtil.hasNotBeenSent(credit?.status);

  const renderGuarantorSection = (guarantorProcesses: AgFinancing['guarantorProcesses']) => {
    if (guarantorProcesses?.length) {
      return <GuarantorsSection guarantorsInfo={guarantorProcesses} disabled={credit?.isDone} />;
    }

    return null;
  };

  const getSerasaData = () => {
    if (serasaDataApi) {
      return creditUtils.mapSerasaReportToCreditInput(serasaDataApi, contactRoles);
    }

    return getPesquisaSerasaInfoFromCredit(credit);
  };

  return credit ? (
    <CreditAnalysisContext.Provider
      value={{
        updateCredit,
        uploadFilesToCredit
      }}
    >
      <PhaseWrapper
        title={
          <>
            <div className="flex">
              <LargeTitle className="mr-3">{i18next.t('creditAnalysis')}</LargeTitle>
              <CreditAnalysisStatusTag status={credit?.status} />
            </div>
            <div className={classes.pollDataWrapper}>
              <Button variant="secondary" size="small" onClick={handlePollInnovationData} busy={loading}>
                {text.toTitleCase(i18next.t('pollData'))}
              </Button>
              {!loading && credit?.innovationDataUpdatedAt ? (
                <LegalBody className={classes.colorGray}>
                  {i18next.t('lastUpdate')}: {utils.date.formatDate(credit?.innovationDataUpdatedAt, 'LL, h:mma')}
                </LegalBody>
              ) : null}
            </div>
          </>
        }
        actionElement={
          <>
            <ToggleOnHoldCreditAnalysis />
            {analysisNotSent ? (
              <>
                <Button
                  size="small"
                  onClick={handleSendAnalysis}
                  disabled={
                    credit?.isDone || !credit?.scoreNumber || !credit?.scoreType || !credit?.exploredAreas?.length
                  }
                >
                  {i18next.t('markComplete')}
                </Button>
                <SendAnalysisModal
                  closeModal={closeSendAnalysisModal}
                  open={sendAnalysisModalOpen}
                  message={i18next.t('askForApprovalFLCMessage')}
                  onConfirmAction={sendAnalysis}
                />
              </>
            ) : (
              undefined
            )}
          </>
        }
      >
        {renderGuarantorSection(agFinance?.guarantorProcesses)}

        <Card className="mt-3">
          <Participation
            participationInfo={creditUtils.getParticipationInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
          <ClientDataSection
            clientDataInfo={creditUtils.getClientDataInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
          <PesquisaSerasa
            pesquisaSerasaInfo={getSerasaData()}
            disabled={credit?.isDone || hasSerasaApiData}
            updateCredit={updateCredit}
            toUploadFiles={uploadFilesToCredit}
          />
          <JudicialActionsSection
            judicialActionsInfo={creditUtils.getJudicialActionsInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
          <IncomeStatementSection
            incomeStatementInfo={creditUtils.getIncomeStatementInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
          <ExploredAreas
            exploredAreasInfo={creditUtils.getExploredAreasInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
          <FinancialInformationSection
            financialInformationInfo={creditUtils.getFinancialInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
          <ScoreSection
            scoreSectionInfo={creditUtils.getConsultingScoreInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
          <NotesSection
            notesSectionInfo={creditUtils.getNotesInfoFromCredit(credit)}
            disabled={credit?.isDone}
            updateCredit={updateCredit}
          />
        </Card>
      </PhaseWrapper>
    </CreditAnalysisContext.Provider>
  ) : (
    <Body>{utils.text.toTitleCase(i18next.t('thisProcessHasNotStartedYet'))}</Body>
  );
};
